import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ImagePlaceholder } from '@components/ui/ImagePlaceholder'
import { Footer } from '@components/Layout'
import { Seo } from '@components/Seo'
import { fetchCaseList } from '@api/cases'
import type { CaseListItem } from '@api/cases'
import { useLang, withLang } from '@shared/i18n/useLang'
import { graphqlLanguageCode } from '@shared/i18n/languages'
import { ssrCache } from '@shared/lib/ssrCache'

export function RabotyPage() {
  const { t } = useTranslation()
  const lang = useLang()
  const code = graphqlLanguageCode(lang)
  const cacheKey = `cases:${code}`
  const seeded = ssrCache.has(cacheKey)
  const cats = t('roboty.categories', { returnObjects: true }) as string[]
  const [filter, setFilter] = useState(cats[0])
  const [items, setItems] = useState<CaseListItem[]>(() => ssrCache.get<CaseListItem[]>(cacheKey) ?? [])
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>(seeded ? 'ok' : 'loading')
  const skipNextFetch = useRef(seeded)

  useEffect(() => {
    if (skipNextFetch.current) { skipNextFetch.current = false; return }
    setStatus('loading')
    fetchCaseList(100, code)
      .then(data => { setItems(data); setStatus('ok') })
      .catch(() => setStatus('error'))
  }, [lang, code])

  const visible = items.filter(c => filter === cats[0] || c.category === filter)

  return (
    <>
      <Seo title={t('seo.raboty.title')} description={t('seo.raboty.description')} />

      {/* Page head */}
      <section className="section-hero-bg" style={{ padding: '64px 0' }}>
        <div className="container-page" style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase' }}>
            <Link to={withLang(lang, '/')} style={{ color: '#5C6670', transition: 'color .15s' }}>{t('common.home')}</Link>
            <span style={{ color: '#3A434D' }}>/</span>
            <span style={{ color: '#93A3AE' }}>{t('roboty.pageTitle')}</span>
          </div>
          <h1 className="h1-page" style={{ margin: 0, fontSize: 44, fontWeight: 750, letterSpacing: '-.015em', color: '#FFFFFF' }}>{t('roboty.pageTitle')}</h1>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.65, color: '#93A3AE', maxWidth: 640 }}>
            {t('roboty.pageSubtitle')}
          </p>
        </div>
      </section>

      {/* Фильтр + сетка */}
      <section style={{ padding: '56px 0 88px' }}>
        <div className="container-page">
          <div style={{ display: 'flex', gap: 8, marginBottom: 36, flexWrap: 'wrap' }}>
            {cats.map(c => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                style={{
                  fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase',
                  padding: '10px 18px', borderRadius: 3, cursor: 'pointer', transition: 'all .15s',
                  background: c === filter ? '#171C22' : '#FFFFFF',
                  color: c === filter ? '#FFFFFF' : '#5C6670',
                  border: `1px solid ${c === filter ? '#171C22' : '#C6CDD3'}`,
                }}
              >
                {c}
              </button>
            ))}
          </div>

          {status === 'loading' && (
            <div style={{ padding: '80px 0', display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                border: '3px solid #D9DEE2',
                borderTopColor: '#189BD5',
                animation: 'spin 0.8s linear infinite',
              }} />
            </div>
          )}

          {status === 'error' && (
            <div style={{ padding: '64px 0', textAlign: 'center', color: '#93A3AE', fontSize: 14 }}>
              {t('roboty.loadError')}
            </div>
          )}

          {status === 'ok' && (
            <>
              <div className="grid-3">
                {visible.map(c => (
                  <Link
                    key={c.slug}
                    to={withLang(lang, `/raboty/${c.slug}`)}
                    className="card card-link"
                    style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', textDecoration: 'none' }}
                  >
                    {c.mainPhoto
                      ? <img src={c.mainPhoto} alt={c.title} loading="lazy" style={{ width: '100%', height: 230, objectFit: 'cover', display: 'block', flexShrink: 0 }} />
                      : <ImagePlaceholder height={230} />}
                    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', color: '#189BD5' }}>{c.category}</span>
                        <span style={{ fontSize: 11.5, color: '#93A3AE', letterSpacing: '.06em' }}>{c.year}</span>
                      </div>
                      <span style={{ fontSize: 17, fontWeight: 650, color: '#171C22' }}>{c.title}</span>
                    </div>
                  </Link>
                ))}
              </div>

              {visible.length === 0 && (
                <div style={{ padding: '64px 0', textAlign: 'center', color: '#93A3AE', fontSize: 14 }}>
                  {t('roboty.emptyCategory')}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA bar */}
      <section style={{ background: '#171C22', padding: '64px 0' }}>
        <div className="container-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 30, fontWeight: 700, letterSpacing: '-.015em', color: '#FFFFFF' }}>{t('roboty.ctaTitle')}</h2>
            <p style={{ margin: 0, fontSize: 15, color: '#93A3AE' }}>{t('roboty.ctaDesc')}</p>
          </div>
          <Link
            to={withLang(lang, '/zayavka')}
            style={{ flexShrink: 0, background: '#189BD5', color: '#FFFFFF', fontSize: 13, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', padding: '16px 32px', borderRadius: 3, transition: 'background .15s' }}
          >
            {t('common.submitApplication')}
          </Link>
        </div>
      </section>

      <Footer />
    </>
  )
}
